import 'babel-polyfill';
import { createBrowserHistory } from "history";
import React from "react";
import 'react-app-polyfill/ie11';
import 'react-app-polyfill/stable';
import ReactDOM from "react-dom";
import { Provider } from "react-redux";
import { BrowserRouter, Switch } from "react-router-dom";
import { routerMiddleware, routerReducer } from "react-router-redux";
import { applyMiddleware, combineReducers, createStore } from "redux";
import createSagaMiddleware from "redux-saga";
import thunk from "redux-thunk";
import App from "./App";
import "./index.css";
import registerServiceWorker from "./registerServiceWorker";
import advancedSecurityReducer from "./store/reducers/advancedSecurity";
import authReducer from "./store/reducers/auth";
import bandwidthReducer from "./store/reducers/bandwidth";
import deviceGroupsReducer from "./store/reducers/devicegroups";
import devicesReducer from "./store/reducers/devices";
import deviceSteeringReducer from './store/reducers/deviceSteering';
import dhcpReducer from "./store/reducers/dhcp";
import dnsReducer from "./store/reducers/dns";
import equipmentReducer from "./store/reducers/equipment";
import logsReducer from "./store/reducers/logs";
import managedReducer from "./store/reducers/managed";
import mobileHomeReducer from "./store/reducers/mobileHome";
import multiAdvanced from "./store/reducers/multiAdvancedHomeWifi";
import propertyManagerReducer from "./store/reducers/propertyManager";
import routerSpeedCheckReducer from "./store/reducers/routerSpeedCheck";
import securityThreatsReducer from "./store/reducers/securityThreats";
import settingsReducer from "./store/reducers/settings";
import specReadyReducer from "./store/reducers/spectrumReady";
import speedBoostReducer from "./store/reducers/speedBoost";
import topologyReducer from "./store/reducers/topology";
import upnpReducer from "./store/reducers/upnp";
import usersReducer from "./store/reducers/users";
import wanReducer from "./store/reducers/wan";
import qoeReducer from './store/reducers/qoe';
import multiRouterReducer from './store/reducers/multiRouter';
import portForwardingReducer from './store/reducers/portForwarding';
import secondSsidReducer from './store/reducers/2ndSSID';
import staticIpReducer from './store/reducers/staticIp';
import propertyIOTNetworkReducer from './store/reducers/IOTNetwork';
import wifiSecurityReducer from './store/reducers/wifiSecurity';
import networkThreadReducer from './store/reducers/networkThread';
import cellularBackupReducer from './store/reducers/cellularBackup';
import staticIPConfigReducer from './store/reducers/staticIPConfig';
import broadcastSettingsReducer from './store/reducers/broadcastSettings';

import {
  watchAdvancedSecurity,
  watchAuth,
  watchBandwidth,
  watchDeviceGroups,
  watchDevices, watchDeviceSteering, watchDhcp,
  watchDns,
  watchEquipment,
  watchLogs,
  watchManaged,
  watchMobileHome,
  watchMultipleAdvancedHomeWifi,
  watchPropertyManager,
  watchRouterSpeedCheck,
  watchSecurityThreats,
  watchSettings,
  watchSpecReady,
  watchSpeedBoostLogs,
  watchTopology,
  watchUpnp,
  watchUsers,
  watchWan,
  watchQoe,
  watchMultiRouter,
  watchPortForwarding,
  watchSecondSsid,
  watchStaticIp,
  watchPropertyIOTNetwork,
  watchWifiSecurity,
  watchNetworkThread,
  watchCellularBackup,
  watchStaticIPConfig,
  watchBroadcastSettings
} from './store/sagas';
import { composeWithDevTools } from '@redux-devtools/extension';

export const history = createBrowserHistory();
const historyMiddleware = routerMiddleware(history);

// ONLY ENABLE IF stack tracing is needed to debug the redux store. It adds a lot of overweight.
const reduxOptions = {
  // trace: true,
}
const composeEnhancers = process.env.NODE_ENV === 'development' ? composeWithDevTools(reduxOptions) : null


const rootReducer = combineReducers({
  users: usersReducer,
  settings: settingsReducer,
  equipment: equipmentReducer,
  wan: wanReducer,
  managed: managedReducer,
  devices: devicesReducer,
  dhcp: dhcpReducer,
  deviceGroups: deviceGroupsReducer,
  dns: dnsReducer,
  upnp: upnpReducer,
  mobileHome: mobileHomeReducer,
  topology: topologyReducer,
  bandwidth: bandwidthReducer,
  auth: authReducer,
  logs: logsReducer,
  speedBoost: speedBoostReducer,
  router: routerReducer,
  spectrumReady: specReadyReducer,
  multiAdvanced: multiAdvanced,
  advancedSecurity: advancedSecurityReducer,
  securityThreats: securityThreatsReducer,
  propertyManager: propertyManagerReducer,
  speedCheck: routerSpeedCheckReducer,
  deviceSteering: deviceSteeringReducer,
  qoe: qoeReducer,
  multiRouter: multiRouterReducer,
  portForwarding: portForwardingReducer,
  secondSsid: secondSsidReducer,
  staticIp: staticIpReducer,
  propertyIOTNetwork: propertyIOTNetworkReducer,
  wifiSecurity: wifiSecurityReducer,
  networkThread: networkThreadReducer,
  cellularBackup: cellularBackupReducer,
  staticIPConfig: staticIPConfigReducer,
  broadcastSettings: broadcastSettingsReducer
});
const sagaMiddleware = createSagaMiddleware();
let store;
if (process.env.NODE_ENV === "development") {
  store = createStore(
    rootReducer,
    composeEnhancers(applyMiddleware(thunk, sagaMiddleware, historyMiddleware))
  );
  window.__store__ = store; // expose for manual testing
} else {
  store = createStore(
    rootReducer,
    applyMiddleware(thunk, sagaMiddleware, historyMiddleware)
  );
}

sagaMiddleware.run(watchAuth);
sagaMiddleware.run(watchSettings);
sagaMiddleware.run(watchEquipment);
sagaMiddleware.run(watchWan);
sagaMiddleware.run(watchSpecReady);
sagaMiddleware.run(watchManaged);
sagaMiddleware.run(watchDevices);
sagaMiddleware.run(watchDeviceGroups);
sagaMiddleware.run(watchDhcp);
sagaMiddleware.run(watchDns);
sagaMiddleware.run(watchUpnp);
sagaMiddleware.run(watchMobileHome);
sagaMiddleware.run(watchTopology);
sagaMiddleware.run(watchBandwidth);
sagaMiddleware.run(watchUsers);
sagaMiddleware.run(watchLogs);
sagaMiddleware.run(watchSpeedBoostLogs);
sagaMiddleware.run(watchMultipleAdvancedHomeWifi);
sagaMiddleware.run(watchAdvancedSecurity);
sagaMiddleware.run(watchSecurityThreats);
sagaMiddleware.run(watchPropertyManager);
sagaMiddleware.run(watchRouterSpeedCheck);
sagaMiddleware.run(watchDeviceSteering);
sagaMiddleware.run(watchQoe);
sagaMiddleware.run(watchMultiRouter);
sagaMiddleware.run(watchPortForwarding);
sagaMiddleware.run(watchSecondSsid);
sagaMiddleware.run(watchStaticIp);
sagaMiddleware.run(watchPropertyIOTNetwork);
sagaMiddleware.run(watchWifiSecurity);
sagaMiddleware.run(watchNetworkThread);
sagaMiddleware.run(watchCellularBackup);
sagaMiddleware.run(watchStaticIPConfig);
sagaMiddleware.run(watchBroadcastSettings);

const app = (
  <Provider store={store}>
    {/* <ConnectedRouter history={history}> */}
    <>
      <BrowserRouter>
        <Switch>
          <App />
        </Switch>
      </BrowserRouter>
    </>
    {/* </ConnectedRouter> */}
  </Provider>
);

ReactDOM.render(app, document.getElementById("root"));
registerServiceWorker.register();

